import React, { useEffect } from 'react';
import { Button, Col, List, Row, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'common/config/dayjs.config';
import { TwoColumnsLayout } from 'common/components/Layouts/TwoColumnsLayout';
import { MobileLayout } from 'common/components/Layouts/MobileLayout';
import { getMapLinkToStore, getStoreAddressString } from 'common/helpers/Store.helper';
import { ERoutesCommon, ERoutesPrivate } from 'common/models/routesModel';
import { ORDERS_PAYMENT_DATE_FORMAT, ORDERS_PICKUP_DATE_FORMAT } from 'common/consts/Orders.const';
import { getCustomerFullName } from 'common/helpers/Customer.helper';
import { StoreRating } from 'common/components/StoreRating';
import { formatAmount, formatStringToShort } from 'common/helpers/format.helper';
import { ReactComponent as SvgPlacePoint } from 'app/assets/images/placePoint.svg';
import { ReactComponent as SvgMapPoint } from 'app/assets/images/mapPoint.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getCurrentOrder } from 'app/store/reducers/order.reducer';
import { OrderPageEmpty } from 'entities/Orders/components/OrderPageEmpty';
import { formatTime } from 'entities/PeakHours/helpers/date.helper';
import { ECommonRejectReasons, EOrderStatus } from 'entities/Orders/Orders.models';
import { OrderItemListItem } from 'entities/Orders/components/OrderItemListItem';
import { getOrderStatusMessage } from 'entities/Orders/Orders.helpers';
import { PromoInfo } from 'entities/Cart/components/PromoInfo';
import { ECartPromoBonusType, ECartPromoType } from 'entities/Cart/Cart.models';
import { CartItemTotal } from 'entities/Cart/components/CartItemTotal';
import { BitecoinOrderWidget } from 'entities/Bitecoin/components/BitecoinOrderWidget';

export const OrderPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { customerModel } = useAppSelector((state) => state.customer);
  const { orderModel, orderModelLoading } = useAppSelector((state) => state.order);
  const {
    items,
    total,
    discountTotal,
    promo,
    bonusTotal,
    code,
    storeData,
    status,
    pickUpTime,
    updatedAt,
    checkoutSession,
    earnedBitecoins,
  } = orderModel || {};

  const isPromoApplied = !!promo;
  const promoBonusType = promo?.bonus?.bonusType as ECartPromoBonusType | undefined;
  const promoType = promo?.type;

  const navigate = useNavigate();

  const orderNumber = code || '';
  const customerName = getCustomerFullName(customerModel);
  const { storeName = 'Unknown store', phone: storePhone = '' } = storeData || {};

  const isFinished = [
    EOrderStatus.RejectedByCustomer,
    EOrderStatus.RejectedByStore,
    EOrderStatus.Finished,
    EOrderStatus.PaymentCancelled,
  ].includes(status as EOrderStatus);

  const isCancelled = status === EOrderStatus.RejectedByStore;
  const isPaymentAwaiting = status === EOrderStatus.PaymentAwaiting;

  const orderStatusLabel = getOrderStatusMessage(status as EOrderStatus, orderModel?.rejectReason as ECommonRejectReasons);

  const time = pickUpTime ? formatTime(dayjs(pickUpTime), ORDERS_PICKUP_DATE_FORMAT) : '';

  const promoValidDate = dayjs(updatedAt).add(7, 'days').format('MMMM D, YYYY');

  const paymentSessionId = checkoutSession ? formatStringToShort(checkoutSession.id) : '';
  const paymentSessionDate = checkoutSession ? dayjs(checkoutSession.createdAt).format(ORDERS_PAYMENT_DATE_FORMAT) : '';

  useEffect(() => {
    dispatch(getCurrentOrder());
  }, []);

  const back = () => {
    navigate(ERoutesCommon.Root);
  };

  const refresh = () => {
    dispatch(getCurrentOrder());
  };

  if (!orderModel) {
    return (
      <Spin spinning={orderModelLoading}>
        <TwoColumnsLayout left={<OrderPageEmpty />} />
      </Spin>
    );
  }

  return (
    <Spin spinning={orderModelLoading}>
      <TwoColumnsLayout
        left={
          <MobileLayout header="Your Order" subHeader={`Order number: ${orderNumber}`}>
            <div className="pl-4 pr-4">
              {isFinished ? (
                <Row justify="space-between" align="middle" className="mb-9">
                  <Col span={24} className="fs-sm font-weight-bold color-orange-main">
                    {orderStatusLabel}
                  </Col>
                </Row>
              ) : (
                <Row justify="space-between" align="middle" className="mb-4">
                  <Col span={12} className="fs-sm font-weight-bold color-orange-main">
                    {orderStatusLabel}
                  </Col>

                  <Col>
                    <Button size="large" className="btn" onClick={refresh} disabled={orderModelLoading}>
                      Refresh
                    </Button>
                  </Col>
                </Row>
              )}

              {isPaymentAwaiting && (
                <div className="fs font-weight-bold color-default mb-7">
                  <div>Payment session ID:</div>
                  <div title={`${checkoutSession?.id} - ${paymentSessionDate}`}>
                    {`${paymentSessionId} - ${paymentSessionDate}`}
                  </div>
                </div>
              )}

              <div className="fs font-weight-bold color-default mb-7">
                <a href={`tel:+${storePhone}`} className="link fs color-default t-decoration-none">
                  Store phone: {storePhone}
                </a>
              </div>

              <div className="font-weight-bold mb-7">
                <div className="fs color-darkCherry">Pick up at</div>
                <div className="fs-xs color-default">{time || '-'}</div>
              </div>

              <div className="order-info-item mb-7">
                <SvgPlacePoint />

                <div>
                  <div className="basic__text_title mb-3 mt-1">Pick up from</div>
                  {storeName && (
                    <div className="fs-xs color-default font-weight-5 lh-sm mb-2">
                      <span>{storeName}</span>
                      <StoreRating store={storeData} className="ml-3" />
                    </div>
                  )}
                  <div className="color-gray fs-xxs lh-sm">{storeData ? getStoreAddressString(storeData) : '-'}</div>
                </div>
              </div>

              {storeData?.address && (
                <div className="order-info-item mb-7">
                  <SvgMapPoint />

                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getMapLinkToStore(storeData)}
                    className="link fs-xs color-orange-main"
                  >
                    View Directions to Store
                  </a>
                </div>
              )}

              <PromoInfo promoType={promoType as ECartPromoType} bonusType={promoBonusType} />

              <div className="font-weight-bold fs-xs color-darkCherry mb-2">Order</div>
            </div>

            <div className="menu-card menu-card_border-light">
              <div className="mb-6 bordered">
                <List
                  dataSource={items}
                  renderItem={(item) => <OrderItemListItem promoBonusType={promoBonusType} item={item} />}
                />
              </div>

              {!!bonusTotal && (
                <Row className="menu-card__header pb-4" justify="space-between" align="middle" wrap={false}>
                  <div>Discount</div>
                  <div className="color-orange-main">P{formatAmount(bonusTotal)}</div>
                </Row>
              )}

              <Row className="menu-card__header" justify="space-between" align="middle" wrap={false}>
                <div>Total</div>
                <CartItemTotal
                  total={total}
                  discountTotal={discountTotal}
                  bonusType={promoBonusType}
                  isPromoApplied={isPromoApplied}
                  bonusTotal={bonusTotal}
                />
              </Row>
            </div>
          </MobileLayout>
        }
        right={
          <MobileLayout>
            {!!earnedBitecoins && !isCancelled && (
              <BitecoinOrderWidget orderStatus={status as EOrderStatus} earnedBitecoins={earnedBitecoins} />
            )}

            {isCancelled && (
              <div className="color-grayDark mb-7">
                <p>We’re terribly sorry. Your order {orderNumber} was cancelled.</p>
                <p>
                  That’s not the experience we want for you. We are fixing the issue and are working to make sure it doesn’t
                  happen again.
                </p>
                <p>
                  To make it up to you, we’re giving you{' '}
                  <span className="font-weight-bold color-orange-main">P20 OFF your next purchase</span> worth at least P200,
                  valid until {promoValidDate}. No promo code needed. Your discount will be automatically applied at checkout if
                  it’s the best available discount for your order. Terms & Conditions apply.
                </p>
              </div>
            )}

            <Button size="large" block className="btn mb-7" type="primary" onClick={back}>
              Back to the main page
            </Button>

            {!isFinished && (
              <div className="color-grayDark">
                <p>
                  Please note that once payment has been processed, we are unable to offer order cancellations as the Store may
                  have already started preparing your order.
                </p>
                <p>
                  Please be at the store on or shortly before {time || 'pick up time'}. Our products are best consumed
                  immediately. If you want to receive real-time updates via SMS, please update your MB Go! profile{' '}
                  <a href={ERoutesPrivate.Settings} className="link fs-xxs color-orange-main t-decoration-none">
                    here
                  </a>
                </p>
                <p>
                  We would like to encourage a positive experience for all users, from our customers to our stores. See you at our
                  stores soon{customerName && `, ${customerName}`}
                </p>
              </div>
            )}
          </MobileLayout>
        }
      />
    </Spin>
  );
};
